import React, { useEffect } from 'react'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { useForm } from 'react-hook-form'
import { Button } from '@mui/material'
import {
  MemberActionType,
  getMemberAction,
  resetMemberAction,
  updateMemberAction,
} from '@manager/reducers/member/memberActions'
import ContentHeader from '@src/components/ContentHeader'
import { MemberDetail } from '@src/manager/models/userDashboard'
import { StoreState } from '@src/manager/reducers'
import { useGlobalSnackbar, useLoading } from '@src/hooks'
import {
  managerPath,
  RouteSection,
  toManagerRoute,
} from '@src/common/appConfig'
import { ReactComponent as IconSave } from '@src/assets/icons/iconSave.svg'
import MemberForm from './components/MemberForm'
import { BREADCRUMBS } from './utils'
import Snackbar, { SnackbarState } from '@src/components/Snackbar'
import commonMessages from '@common/messages'
import messages from '@manager/utils/messages'
import { ErrorResponse } from '@src/utils/type'

export default function MemberEdit() {
  const { formatMessage: f } = useIntl()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { currentMember } = useSelector((state: StoreState) => state.member)
  const { id, email } = useParams()
  const { control, handleSubmit, reset } = useForm<MemberDetail>({
    defaultValues: currentMember,
  })
  const { status, setSnackbar, clearSnackbar } = useGlobalSnackbar({
    page: RouteSection.UserDashboard,
  })
  const { openSpinner } = useLoading({
    endActions: {
      success: [
        MemberActionType.GET_MEMBER_SUCCESS,
        MemberActionType.UPDATE_MEMBER_SUCCESS,
      ],
      error: [
        MemberActionType.GET_MEMBER_ERROR,
        MemberActionType.UPDATE_MEMBER_ERROR,
      ],
    },
    cbk: {
      success: ({ type }: { type: MemberActionType }) => {
        if (type === MemberActionType.UPDATE_MEMBER_SUCCESS) {
          setSnackbar({
            state: SnackbarState.Success,
            message: f(commonMessages.updateSuccess),
            notKeepData: false,
          })
          document.documentElement.scrollTop = 0
          navigate(
            generatePath(toManagerRoute(managerPath.userDashboard.dashboard), {
              id,
            })
          )
        }
      },
      error: ({
        type,
        error,
      }: {
        type: MemberActionType
        error: ErrorResponse
      }) => {
        const msg =
          type === MemberActionType.UPDATE_MEMBER_ERROR
            ? f(commonMessages.updateFailed)
            : error?.message ?? f(commonMessages.unknownError)
        setSnackbar({
          state: SnackbarState.Error,
          message: msg,
          notKeepData: true,
        })
        document.documentElement.scrollTop = 0
      },
    },
  })

  const onSubmit = (data: MemberDetail) => {
    openSpinner()
    dispatch(updateMemberAction({ id: id!, email: email!, data }))
  }

  useEffect(() => {
    openSpinner()
    dispatch(getMemberAction({ id: id!, email: email! }))
  }, [dispatch, openSpinner, id, email])

  useEffect(() => {
    if (!!currentMember) {
      reset(currentMember)
    }
  }, [currentMember, reset])

  useEffect(
    () => () => {
      dispatch(resetMemberAction())
    },
    [dispatch]
  )

  return (
    <>
      {status && (
        <Snackbar
          severity={SnackbarState.Error}
          message={status.message!}
          onClose={clearSnackbar}
        />
      )}
      {currentMember && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <ContentHeader
            title={f(messages.memberEdit)}
            breadcrumbs={[
              ...BREADCRUMBS,
              {
                title: messages.userDashboard,
                route: generatePath(
                  toManagerRoute(managerPath.userDashboard.dashboard),
                  { id }
                ),
              },
              {
                title: messages.memberEdit,
              },
            ]}
            buttonList={[
              <Button
                variant="contained"
                type="submit"
                startIcon={<IconSave />}>
                {f(commonMessages.save)}
              </Button>,
            ]}
          />
          <MemberForm control={control} />
        </form>
      )}
    </>
  )
}
